// External libs
import React, { useEffect, useState , useRef } from "react";
import PropTypes from "prop-types";

// Constants
import { backend_request_interval_ms } from "../../../constants/BackendRoutes.js";

// Components
import { fetchDeviceReadings} from "../../../services/deviceService.js";
import WidgetGraphline from "./WidgetGraphLine.js";
import {exportReadingCsv} from "../../../utils/exportCsv.js"

/**
 * The SensorReadingsChartWidget component.
 * Fetches sensor readings for a device and displays them as a graph using `WidgetGraphline`.
 * It handles multiple keys for different sensors and fetches data periodically.
 *
 * @param {string} title - Display title for the graph
 * @param {string} deviceId - Device identifier.
 * @param {Array} keys - An array of sensor keys to fetch.
 */
const SensorReadingsChartWidget = ({
  title,
  deviceId,
  keys,
}) => {

  // State hook to store the fetched reading data.
  const [readingData, setReadingData] = useState([]);

  // State hook to store the parsed sensor data formatted for the graph.
  const [sensorData, setSensorData] = useState([]);

  // Flag to tell if chart Data is fetching from backend
  const isFetching = useRef(false);

  const formatDateTimeLocal = (date) => {
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60 * 1000);

    return localDate.toISOString().slice(0, 19);
  };

  // State hook for the date picker
  const now = new Date();
  const [startDateTime, setStartDateTime] = useState(
      formatDateTimeLocal(new Date(now.getTime() - 60 * 60 * 1000))
  );

  const [endDateTime, setEndDateTime] = useState(
      formatDateTimeLocal(now)
  );


  // Effect hook to update the sensorData state whenever readingData changes.
  useEffect(() => {
    if (readingData != null) {
      setSensorData(parseSensorData(readingData));
    }
  }, [readingData]);

  // Effect hook to update the timeRange and fetching the data from Backend api
  useEffect(() => {
    console.log(`TimeRange got ${`startDateTime:${startDateTime}, endDateTime:${endDateTime}`}`)
    async function loadReadings() {
      // Ignore concurrent requests
      if (isFetching.current) return;

      isFetching.current = true;

      try {
        await fetchDeviceReadingsAndSetReadingData();
      } catch (error) {
        console.error("Failed to fetch readings:", error);
      } finally {
        isFetching.current = false;
      }
    }
    loadReadings();
  }, [startDateTime, endDateTime]);

  // Effect hook to initiate Date Range Picker change, such that will trigger Chart Data Fetching
  useEffect(() => {
    const updateDateRange  = () => {
      const now = new Date();
      setStartDateTime(formatDateTimeLocal(new Date(now.getTime() - 60 * 60 * 1000)));
      setEndDateTime(formatDateTimeLocal(now));
    }

    updateDateRange();
    const intervalId = setInterval(updateDateRange, backend_request_interval_ms);

    return () => clearInterval(intervalId);
  }, []);

  /**
   * Parses the fetched sensor data into a format suitable for the graph.
   *
   * @param {Array} data - The raw data fetched from the backend.
   * @returns {Array} Parsed sensor data formatted for graph rendering.
   */
  const parseSensorData = (data) => {
    const result = {};
    const maxLength = 100;

    // only show the latest 100 points for the display
    const endIndex = Math.min(maxLength, data.length);
    for (let i = 0; i < endIndex; i++) {
      const { key, value, units, timestamp } = data[i];
      const id = `${key} (${units})`;
      const yAxis = key === keys[0] ? "left" : "right";
      const x = new Date(timestamp);
      const y = value;

      if (!result[id]) {
        result[id] = { id, yAxis, data: [] };
      }

      result[id].data.push({ x, y });
    };

    return Object.values(result);
  };

  
  /**
   * Fetches the latest sensor readings from the backend and updates the readingData state.
   *
   * @returns {Promise<void>} A promise that resolves when the data is fetched and state is updated.
   */
  const fetchDeviceReadingsAndSetReadingData = async () => {
    const data = await fetchDeviceReadings(deviceId, startDateTime, endDateTime, "all", keys);
    setReadingData(data);
  };

  /**
   * Generates a message when no device is selected for the graph.
   *
   * @returns {string} The message string to be displayed when no device is selected.
   */
  const getEmptySentence = () => {
    let string = `Select device for graph of ${keys[0]}`;
    if (keys.length > 1) {
      string = `${string} and ${keys[1]}`;
    }
    string = `${string}...`;
    return string;
  };

  /**
   * Generates a message when the device has no data for the selected keys and timeRange.
   *
   * @returns {string} The message string to be displayed when no data is available.
   */
  const getDeviceDataEmptySentence = () => {
    let string = `Device ${deviceId} has no data available for ${keys[0]}`;
    if (keys.length > 1) {
      string = `${string} and ${keys[1]}`;
    }

    string = `${string} for the selected period`;

    return string;
  };

  /**
   * Renders the readings or an appropriate message depending on the available data.
   *
   * @param {Array|null} readings - The fetched sensor readings data.
   * @returns {JSX.Element} A message or the `WidgetGraphline` component displaying the data.
   */
  const DisplayReadings = (sensorData) => {

    useEffect(() => {
      console.log("Component mounted with new data!");
      // Run initialization logic here
    }, [sensorData]);

    if (deviceId === null) {
      return <div className="widget-text-empty">{getEmptySentence()}</div>;
    }
    if (sensorData.length === 0) {
      return <div className="widget-text-empty">{getDeviceDataEmptySentence()}</div>;
    }
    return <WidgetGraphline data={sensorData} />;
  };

  const handleExportCsv = async () => {
    exportReadingCsv(readingData,`${title}-${deviceId}-${keys}.csv`);
  };

  const handleStartDateTimeSet = (value) => {

    setStartDateTime(value)

    if (endDateTime < value) {
      setEndDateTime(value);
    }
  }

  // final return for the display as a whole Sensor Graph widget
  return (
    <div className="sensor-readings-widget">
      <div className="widget-header">
        <h3>
          {title}
        </h3>
          <label>
              Start: <input
                  type="datetime-local"
                  value={startDateTime}
                  onChange={(e) => handleStartDateTimeSet(e.target.value)}
              />
          </label>

          <label>
              End: <input
                  type="datetime-local"
                  value={endDateTime}
                  min={startDateTime}
                  onChange={(e) => setEndDateTime(e.target.value)}
              />
          </label>
        <button className="submit-btn" onClick={handleExportCsv}>
          Export CSV
        </button>
      </div>
      <div>
        {DisplayReadings(sensorData)}
      </div>
    </div>
  );
};

SensorReadingsChartWidget.propTypes = {
  deviceId: PropTypes.string.isRequired,
  keys: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default SensorReadingsChartWidget;
